/**
 * Turns raw technical errors (Postgres codes, network failures, Supabase
 * messages) into one calm, plain-English sentence that tells the person what
 * happened and what to do next. Never shows codes or developer wording.
 */

type AnyError =
  | { message?: string | null; code?: string | null; details?: string | null; hint?: string | null }
  | string
  | null
  | undefined;

const EXPLORE_RULE = {
  test: /explore|sample nursery/i,
  message:
    "This is a sample nursery for exploring. Upgrading unlocks full editing, and real exports, for your own nursery.",
};

const RULES: Array<{ test: RegExp; message: string }> = [
  {
    test: /failed to fetch|network ?error|networkerror|load failed|err_internet|offline/i,
    message: "We could not reach Nursly just then. Check your internet connection and try again.",
  },
  {
    test: /timeout|timed out|deadline|statement canceled/i,
    message: "That took longer than expected and stopped. Please try again in a moment.",
  },
  {
    test: /rate limit|too many requests|429/i,
    message: "You have tried that a few times in quick succession. Please wait a minute, then try again.",
  },
  {
    test: /jwt|token is expired|refresh token|session|not authenticated|401/i,
    message: "You have been signed out. Please sign in again and we will bring you back here.",
  },
  {
    test: /row-level security|permission denied|not authorized|forbidden|403|violates policy/i,
    message: "You do not have permission to do this. Ask an admin at your nursery to make this change.",
  },
  {
    test: /duplicate key|already exists|unique constraint/i,
    message: "This already exists, so it was not added again. Search for it first, then edit the existing one.",
  },
  {
    test: /foreign key|violates foreign key/i,
    message: "This is linked to something else, so it cannot be changed on its own. Remove the link first, then try again.",
  },
  {
    test: /not-null|null value in column|violates check constraint|invalid input syntax/i,
    message: "Something on the form was not filled in correctly. Check the highlighted fields and save again.",
  },
  {
    test: /no rows|not found|404/i,
    message: "We could not find that. It may have been changed or removed by someone else. Refresh the page and try again.",
  },
  {
    test: /storage|payload too large|413|file/i,
    message: "That file could not be uploaded. Try a smaller file, or a PDF, JPG or PNG.",
  },
];

const FALLBACK =
  "Something went wrong and this was not saved. Please try again. If it keeps happening, email support@nursly.co.uk.";

/** Plain-English sentence for any error. Safe to pass anything. */
export function friendlyError(error: AnyError, fallback: string = FALLBACK): string {
  const raw =
    typeof error === "string"
      ? error
      : [error?.message, error?.details, error?.hint, error?.code].filter(Boolean).join(" ");

  if (!raw) return fallback;

  if (EXPLORE_RULE.test.test(raw)) return EXPLORE_RULE.message;

  for (const rule of RULES) {
    if (rule.test.test(raw)) return rule.message;
  }

  // A short, sentence-like message with no technical markers is safe to show.
  const looksTechnical = /[{}<>[\]]|_[a-z]|::|\bpg[a-z_]*\b|\berror\b.*\bcode\b/i.test(raw);
  if (!looksTechnical && raw.length <= 140) return raw;

  return fallback;
}
